import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  format, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, eachDayOfInterval,
  isSameMonth, isSameDay, isToday, addMonths, subMonths,
} from "date-fns";
import {
  ChevronLeft, ChevronRight, CalendarDays,
  Phone, User, AlertCircle, X, ExternalLink,
  CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import { buildCityRegionMap, regionChipStyle, regionForItem } from "@/lib/regionColors";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const EXCLUDED_STATUSES = new Set(["Confirmed", "Closed Won", "Converted", "Declined", "Closed Lost", "Closed", "Invalid"]);

const STATUS_PILL = {
  "New":             "bg-sky-100 text-sky-700",
  "Contacted":       "bg-indigo-100 text-indigo-700",
  "Interested":      "bg-violet-100 text-violet-700",
  "Follow Up":       "bg-yellow-100 text-yellow-700",
  "Quotation Sent":  "bg-orange-100 text-orange-700",
  "Closed Won":      "bg-green-100 text-green-700",
  "Closed Lost":     "bg-red-100 text-red-700",
  "Converted":       "bg-emerald-100 text-emerald-700",
};

/* ── Main page ─────────────────────────────────────────────────────────── */
export default function FollowUpCalendarPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [cityRegionMap, setCityRegionMap] = useState({});
  const [relationshipManagers, setRelationshipManagers] = useState([]);
  const [selectedRM, setSelectedRM] = useState("all");
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/leads`, { withCredentials: true }),
      axios.get(`${API}/cities`, { withCredentials: true }),
      axios.get(`${API}/users/relationship-managers`, { withCredentials: true }),
    ])
      .then(([leadsResponse, citiesResponse, managersResponse]) => {
        setLeads(leadsResponse.data);
        setCityRegionMap(buildCityRegionMap(citiesResponse.data));
        setRelationshipManagers(managersResponse.data);
      })
      .catch(() => toast.error("Failed to load follow-ups."))
      .finally(() => setLoading(false));
  }, []);

  /* Build date → leads map */
  const followupMap = useMemo(() => {
    const map = {};
    leads.forEach(lead => {
      if (EXCLUDED_STATUSES.has(lead.status)) return;
      if (selectedRM === "unassigned" && (lead.assigned_to || lead.assigned_to_name)) return;
      if (selectedRM !== "all" && selectedRM !== "unassigned") {
        const manager = relationshipManagers.find(item => String(item.id) === selectedRM);
        const matchesId = String(lead.assigned_to || "") === selectedRM;
        const matchesName = manager?.name && lead.assigned_to_name?.trim().toLocaleLowerCase() === manager.name.trim().toLocaleLowerCase();
        if (!matchesId && !matchesName) return;
      }
      if (!lead.next_followup_date) return;
      if (!map[lead.next_followup_date]) map[lead.next_followup_date] = [];
      map[lead.next_followup_date].push(lead);
    });
    return map;
  }, [leads, selectedRM, relationshipManagers]);

  const todayStr = format(new Date(), "yyyy-MM-dd");

  /* Stats */
  const totalThisMonth = Object.entries(followupMap)
    .filter(([d]) => d.startsWith(format(currentMonth, "yyyy-MM")))
    .reduce((s, [, arr]) => s + arr.length, 0);

  const overdueCount = Object.entries(followupMap)
    .filter(([d]) => d < todayStr)
    .reduce((s, [, arr]) => s + arr.length, 0);

  const todayCount = (followupMap[todayStr] || []).length;

  /* Calendar grid */
  const monthStart = startOfMonth(currentMonth);
  const monthEnd   = endOfMonth(currentMonth);
  const calDays    = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 1 }),
    end:   endOfWeek(monthEnd,   { weekStartsOn: 1 }),
  });

  const selectedDayStr  = selectedDay ? format(selectedDay, "yyyy-MM-dd") : null;
  const selectedLeads   = selectedDayStr ? (followupMap[selectedDayStr] || []) : [];
  const selectedIsOverdue = selectedDayStr && selectedDayStr < todayStr;
  const calendarRegions = useMemo(
    () => Array.from(new Map(Object.values(cityRegionMap).map(region => [region.id, region])).values()),
    [cityRegionMap],
  );

  return (
    <div className="h-[calc(100vh-7rem)] min-h-0 overflow-hidden flex flex-col bg-[#F8FAFC]">

      {/* ── Page header ── */}
      <div className="bg-[#1B2A6B] px-6 py-5 flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-white font-black tracking-tighter text-2xl flex items-center gap-2">
            <CalendarClock size={22} className="text-[#FFD700]" />
            Follow-Up Calendar
          </h1>
          <p className="text-white/60 text-xs mt-0.5">All scheduled follow-ups across leads</p>
        </div>
        <div className="flex items-center gap-5">
          <label className="space-y-1">
            <span className="block text-[9px] font-bold uppercase tracking-widest text-white/60">Relationship Manager</span>
            <select
              value={selectedRM}
              onChange={event => { setSelectedRM(event.target.value); setSelectedDay(null); }}
              className="h-9 min-w-[190px] border border-white/20 bg-white px-3 text-xs font-semibold text-[#1B2A6B] focus:outline-none"
              data-testid="followup-rm-filter"
            >
              <option value="all">All Relationship Managers</option>
              <option value="unassigned">Unassigned</option>
              {relationshipManagers.map(manager => <option key={manager.id} value={String(manager.id)}>{manager.name}</option>)}
            </select>
          </label>
          <Stat label="This Month" value={totalThisMonth} color="text-[#FFD700]" />
          <div className="w-px h-8 bg-white/20" />
          <Stat label="Overdue"    value={overdueCount}   color="text-orange-400" />
          <div className="w-px h-8 bg-white/20" />
          <Stat label="Today"      value={todayCount}     color="text-green-400" />
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* ── Left: calendar grid ── */}
        <div className="flex-1 min-h-0 p-6 overflow-y-auto">
          {/* Month nav */}
          <div className="flex items-center justify-between mb-5">
            <button
              onClick={() => setCurrentMonth(m => subMonths(m, 1))}
              className="p-2 rounded-sm hover:bg-[#E2E8F0] transition-colors"
              data-testid="prev-month-btn"
            >
              <ChevronLeft size={16} className="text-[#475569]" />
            </button>
            <h2 className="text-sm font-black uppercase tracking-widest text-[#0F172A]">
              {format(currentMonth, "MMMM yyyy")}
            </h2>
            <button
              onClick={() => setCurrentMonth(m => addMonths(m, 1))}
              className="p-2 rounded-sm hover:bg-[#E2E8F0] transition-colors"
              data-testid="next-month-btn"
            >
              <ChevronRight size={16} className="text-[#475569]" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
              <div key={d} className="text-center text-[10px] font-bold uppercase tracking-widest text-[#94A3B8] py-2">
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          {loading ? (
            <div className="flex items-center justify-center h-64 text-sm text-[#94A3B8]">Loading…</div>
          ) : (
            <div className="grid grid-cols-7 gap-px bg-[#E2E8F0]">
              {calDays.map(day => {
                const dayStr      = format(day, "yyyy-MM-dd");
                const dayLeads    = followupMap[dayStr] || [];
                const inMonth     = isSameMonth(day, currentMonth);
                const todayFlag   = isToday(day);
                const selFlag     = selectedDay && isSameDay(day, selectedDay);
                const overdue     = dayStr < todayStr && dayLeads.length > 0;

                return (
                  <button
                    key={dayStr}
                    data-testid={`cal-day-${dayStr}`}
                    onClick={() => setSelectedDay(selFlag ? null : day)}
                    className={`
                      min-h-[88px] p-2 text-left transition-colors
                      ${!inMonth ? "bg-[#F8FAFC]" : selFlag ? "bg-[#EEF2FF]" : overdue ? "bg-orange-50" : "bg-white"}
                      ${selFlag ? "ring-2 ring-inset ring-[#1B2A6B]" : ""}
                      hover:bg-[#F0F4FF]
                    `}
                  >
                    {/* Day number */}
                    <span className={`
                      inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full mb-1
                      ${todayFlag ? "bg-[#1B2A6B] text-[#FFD700]" : inMonth ? "text-[#0F172A]" : "text-[#CBD5E1]"}
                    `}>
                      {format(day, "d")}
                    </span>

                    {/* Lead chips */}
                    {inMonth && dayLeads.length > 0 && (
                      <div className="space-y-0.5">
                        {dayLeads.slice(0, 2).map((l, i) => (
                          <div
                            key={i}
                            style={regionChipStyle(regionForItem(cityRegionMap, l))}
                            className={`text-[9px] font-semibold px-1 py-0.5 truncate leading-tight
                              ${regionForItem(cityRegionMap, l) ? "" : overdue ? "bg-orange-100 text-orange-800" : "bg-[#EEF2FF] text-[#1B2A6B]"}`}
                          >
                            {l.customer_name}
                          </div>
                        ))}
                        {dayLeads.length > 2 && (
                          <p className="text-[9px] text-[#94A3B8] font-semibold pl-1">
                            +{dayLeads.length - 2} more
                          </p>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-x-5 gap-y-2 mt-4 flex-wrap">
            {calendarRegions.map(region => (
              <div key={region.id} className="flex items-center gap-1.5 text-[10px] text-[#475569] font-medium">
                <span className="w-3 h-3 border border-black/10" style={{ backgroundColor: region.color }} />
                {region.name}
              </div>
            ))}
            <div className="flex items-center gap-1.5 text-[10px] text-[#475569] font-medium"><span className="w-3 h-3 bg-orange-100 border border-orange-300" />Overdue day</div>
            <div className="flex items-center gap-1.5 text-[10px] text-[#475569] font-medium"><span className="w-3 h-3 bg-[#FFFBEB] border border-[#FFD700]" />Today</div>
          </div>
        </div>

        {/* ── Right: day detail panel ── */}
        <div className="w-[380px] min-h-0 border-l border-[#E2E8F0] bg-white flex flex-col overflow-hidden">
          {!selectedDay ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-3">
              <CalendarDays size={40} className="text-[#E2E8F0]" />
              <p className="text-sm font-semibold text-[#475569]">Click a day to see follow-ups</p>
              <p className="text-xs text-[#94A3B8]">Days with coloured chips have scheduled follow-ups</p>
            </div>
          ) : (
            <>
              {/* Panel header */}
              <div className={`px-5 py-4 border-b border-[#E2E8F0] shrink-0 flex items-start justify-between
                ${selectedIsOverdue ? "bg-orange-50" : "bg-[#F9FAFB]"}`}>
                <div>
                  <p className="text-sm font-black text-[#0F172A]">
                    {format(selectedDay, "EEEE, d MMMM yyyy")}
                  </p>
                  <p className="text-[10px] text-[#94A3B8] mt-0.5">
                    {selectedLeads.length === 0
                      ? "No follow-ups scheduled"
                      : `${selectedLeads.length} follow-up${selectedLeads.length !== 1 ? "s" : ""}`}
                    {selectedIsOverdue && (
                      <span className="ml-2 text-orange-600 font-bold">· OVERDUE</span>
                    )}
                  </p>
                </div>
                <button onClick={() => setSelectedDay(null)} className="text-[#94A3B8] hover:text-[#475569] p-1 mt-0.5">
                  <X size={15} />
                </button>
              </div>

              {/* Lead cards */}
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-3">
                {selectedLeads.length === 0 ? (
                  <div className="text-center py-10">
                    <p className="text-sm text-[#94A3B8]">No follow-ups on this day</p>
                  </div>
                ) : (
                  selectedLeads.map(lead => (
                    <FollowUpCard
                      key={lead.id}
                      lead={lead}
                      region={regionForItem(cityRegionMap, lead)}
                      isOverdue={selectedIsOverdue}
                      onNavigate={() => navigate(`/leads/${lead.id}`)}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Stat badge ── */
function Stat({ label, value, color }) {
  return (
    <div className="text-center">
      <p className={`text-2xl font-black leading-none ${color}`}>{value}</p>
      <p className="text-white/60 text-[10px] uppercase tracking-widest mt-0.5">{label}</p>
    </div>
  );
}

/* ── Single follow-up card ── */
function FollowUpCard({ lead, region, isOverdue, onNavigate }) {
  return (
    <div className="border border-[#E2E8F0] hover:border-[#1B2A6B] transition-colors" style={region ? { borderTop: `3px solid ${region.color}` } : undefined}>
      {isOverdue && (
        <div className="bg-orange-500 px-3 py-1 flex items-center gap-1.5">
          <AlertCircle size={10} className="text-white" />
          <span className="text-[10px] font-bold text-white uppercase tracking-wider">Overdue</span>
        </div>
      )}
      <div className="p-3">
        {/* Name + status */}
        <div className="flex items-start justify-between gap-2 mb-2.5">
          <button
            data-testid={`followup-lead-link-${lead.id}`}
            onClick={onNavigate}
            className="text-sm font-bold text-[#1B2A6B] hover:underline underline-offset-2 text-left flex items-center gap-1"
          >
            {lead.customer_name}
            <ExternalLink size={11} className="text-[#94A3B8] shrink-0" />
          </button>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 whitespace-nowrap ${STATUS_PILL[lead.status] || "bg-gray-100 text-gray-700"}`}>
            {lead.status}
          </span>
        </div>

        {/* Meta */}
        <div className="space-y-1.5">
          {lead.phone && (
            <div className="flex items-center gap-1.5 text-xs text-[#475569]">
              <Phone size={11} className="text-[#94A3B8] shrink-0" />
              <span>{lead.phone}</span>
            </div>
          )}
          {lead.assigned_to_name && (
            <div className="flex items-center gap-1.5 text-xs text-[#475569]">
              <User size={11} className="text-[#94A3B8] shrink-0" />
              <span>{lead.assigned_to_name}</span>
            </div>
          )}
          {region && (
            <div className="flex items-center gap-1.5 text-xs text-[#475569]">
              <span className="w-2.5 h-2.5 rounded-full border border-black/10" style={{ backgroundColor: region.color }} />
              <span>{region.name}</span>
            </div>
          )}

          {/* Action box */}
          {lead.next_followup_action && (
            <div className="mt-2 bg-[#F0F4FF] border border-[#C7D2FE] px-2.5 py-2">
              <p className="text-[9px] font-bold text-[#1B2A6B] uppercase tracking-wider mb-0.5">Action</p>
              <p className="text-xs text-[#475569]">{lead.next_followup_action}</p>
            </div>
          )}

          {/* Comments */}
          {lead.next_followup_comments && (
            <div className="px-2.5 py-1.5 bg-[#F8FAFC] border border-[#E2E8F0]">
              <p className="text-[10px] text-[#94A3B8] italic">&ldquo;{lead.next_followup_comments}&rdquo;</p>
            </div>
          )}
        </div>

        <button
          onClick={onNavigate}
          className="mt-3 w-full text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B] border border-[#1B2A6B] hover:bg-[#1B2A6B] hover:text-white transition-colors py-1.5 flex items-center justify-center gap-1.5"
        >
          <ExternalLink size={10} />
          Open Lead
        </button>
      </div>
    </div>
  );
}
